import { Button } from '@shared/components/ui/button';
import { Input } from '@shared/components/ui/input';
import { Label } from '@shared/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@shared/components/ui/shadcn/dialog';
import { useToast } from '@shared/hooks/useToast';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCreateTeamContact } from '../../hooks/useCreateTeamContact';

interface AddContactModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  teamId: number;
  onSuccess?: () => void;
}

export const AddContactModal = ({ open, onOpenChange, teamId, onSuccess }: AddContactModalProps) => {
  const { t } = useTranslation('viewClientModule');
  const { toast } = useToast();
  const { createContactAsync, isPending, reset } = useCreateTeamContact(teamId);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    role: '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  // Reset form when modal opens
  useEffect(() => {
    if (open) {
      setFormData({ name: '', email: '', phone: '', role: '' });
      setErrors({});
    }
  }, [open]);

  const handleChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = t('tabs.contacts.modal.errors.nameRequired');
    }

    if (formData.email && formData.email.trim()) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        newErrors.email = t('tabs.contacts.modal.errors.emailInvalid');
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleClose = () => {
    setFormData({ name: '', email: '', phone: '', role: '' });
    setErrors({});
    reset();
    onOpenChange(false);
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      await createContactAsync({
        name: formData.name.trim(),
        email: formData.email.trim() || undefined,
        phone: formData.phone.trim() || undefined,
        role: formData.role.trim() || undefined,
      });

      toast({
        title: t('tabs.contacts.modal.success.title'),
        description: t('tabs.contacts.modal.success.description'),
      });

      onSuccess?.();
      handleClose();
    } catch (error: any) {
      // Handle validation errors from server
      if (error?.errors) {
        const serverErrors: Record<string, string> = {};
        Object.entries(error.errors).forEach(([field, messages]) => {
          serverErrors[field] = Array.isArray(messages) ? messages[0] : String(messages);
        });
        setErrors(serverErrors);
      } else {
        toast({
          title: t('tabs.contacts.modal.error.title'),
          description: t('tabs.contacts.modal.error.description'),
          variant: 'destructive',
        });
      }
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{t('tabs.contacts.modal.title')}</DialogTitle>
          <DialogDescription>{t('tabs.contacts.modal.description')}</DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label htmlFor="contact-name">
              {t('tabs.contacts.name')} <span className="text-destructive">*</span>
            </Label>
            <Input
              id="contact-name"
              name="contact-name"
              autoComplete="off"
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              placeholder={t('tabs.contacts.modal.namePlaceholder')}
              className={errors.name ? 'border-destructive' : ''}
            />
            {errors.name && <p className="text-sm text-destructive">{errors.name}</p>}
          </div>

          <div className="grid gap-2">
            <Label htmlFor="contact-email">{t('tabs.contacts.email')}</Label>
            <Input
              id="contact-email"
              name="contact-email"
              type="email"
              autoComplete="off"
              value={formData.email}
              onChange={(e) => handleChange('email', e.target.value)}
              placeholder={t('tabs.contacts.modal.emailPlaceholder')}
              className={errors.email ? 'border-destructive' : ''}
            />
            {errors.email && <p className="text-sm text-destructive">{errors.email}</p>}
          </div>

          <div className="grid gap-2">
            <Label htmlFor="contact-phone">{t('tabs.contacts.phone')}</Label>
            <Input
              id="contact-phone"
              name="contact-phone"
              type="tel"
              autoComplete="off"
              value={formData.phone}
              onChange={(e) => handleChange('phone', e.target.value)}
              placeholder={t('tabs.contacts.modal.phonePlaceholder')}
              className={errors.phone ? 'border-destructive' : ''}
            />
            {errors.phone && <p className="text-sm text-destructive">{errors.phone}</p>}
          </div>

          <div className="grid gap-2">
            <Label htmlFor="contact-role">{t('tabs.contacts.role')}</Label>
            <Input
              id="contact-role"
              name="contact-role"
              autoComplete="off"
              value={formData.role}
              onChange={(e) => handleChange('role', e.target.value)}
              placeholder={t('tabs.contacts.modal.rolePlaceholder')}
              className={errors.role ? 'border-destructive' : ''}
            />
            {errors.role && <p className="text-sm text-destructive">{errors.role}</p>}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={isPending}>
            {t('tabs.contacts.modal.cancel')}
          </Button>
          <Button variant="primary" onClick={handleSubmit} disabled={isPending || !formData.name.trim()}>
            {isPending ? t('tabs.contacts.modal.saving') : t('tabs.contacts.modal.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
